import BoldSmallText from '@/components/BoldSmallText';
import { USER_GUIDE_LINK } from '@/constants/path';
import { description } from '@/constants/string';
import { Icon, InlineGrid, Link, List, TextField, Tooltip } from '@shopify/polaris';
import { memo, useMemo } from 'react';
import { InfoIcon } from '../Icons';
import RegularText from '../RegularText';
import WarningBanner from '../WarningBanner';
import CustomCard from '../customCard';
import { ExternalIcon } from '@shopify/polaris-icons';

interface IProps {
  orderToShipDays: string;
  prepDays: string;
  shipToReceiveDays: string;
  estDays: string;
  onChangeOrderToShipDays: (value: string) => void;
  onChangePrepDays: (value: string) => void;
  onChangeShipToReceiveDays: (value: string) => void;
  onChangeEstDays: (value: string) => void;
  additionalContent?: React.ReactNode;
  required?: boolean;
  isBlueTitle?: boolean;
}

const EstimatedDateSetup = (props: IProps) => {
  const minPrep = Number(props.orderToShipDays);
  const maxPrep = Number(props.prepDays);
  const minDelivery = Number(props.shipToReceiveDays);
  const maxDelivery = Number(props.estDays);
  const isShowWarning = useMemo(() => {
    return maxPrep < minPrep || minDelivery < minPrep || maxDelivery < minPrep || maxDelivery < minDelivery;
  }, [maxDelivery, maxPrep, minDelivery, minPrep]);

  return (
    <div className="mt-8">
      <CustomCard
        textClassName={props.isBlueTitle ? 'text--blue bold-text-styled' : ''}
        title={'Estimated date setup'}
        required={props.required}
      >
        <div className="mt-8">
          <RegularText>
            This will affect the estimated text and timeline graphic on your store. Only working days are counted. For details,
            read{' '}
            <Link target="_blank" removeUnderline url={USER_GUIDE_LINK.DATE_SETUP} monochrome>
              <div className="inline-flex">
                User guide.
                <Icon source={ExternalIcon} />
              </div>
            </Link>
          </RegularText>
        </div>

        <div className="mt-8">
          <WarningBanner isVisible={isShowWarning} tone="warning" removeMargin>
            <RegularText>This doesn’t seem correct. Try setting:</RegularText>
            <List>
              {maxPrep < minPrep ? (
                <List.Item>
                  <BoldSmallText>Maximum preparation days</BoldSmallText> should be{' '}
                  <BoldSmallText>equal or greater</BoldSmallText> than <BoldSmallText>minimum preparation days.</BoldSmallText>
                </List.Item>
              ) : null}
              {minDelivery < minPrep ? (
                <List.Item>
                  <BoldSmallText>Minimum delivery days</BoldSmallText> should be <BoldSmallText>equal or greater</BoldSmallText>{' '}
                  than <BoldSmallText>minimum preparation days.</BoldSmallText>
                </List.Item>
              ) : null}
              {maxDelivery < minPrep ? (
                <List.Item>
                  <BoldSmallText>Maximum delivery days </BoldSmallText> should be <BoldSmallText>equal or greater</BoldSmallText>{' '}
                  than <BoldSmallText>minimum preparation days.</BoldSmallText>
                </List.Item>
              ) : null}
              {maxDelivery < minDelivery ? (
                <List.Item>
                  <BoldSmallText>Maximum delivery days</BoldSmallText> should be <BoldSmallText>equal or greater</BoldSmallText>{' '}
                  than <BoldSmallText>minimum delivery days.</BoldSmallText>
                </List.Item>
              ) : null}
            </List>
          </WarningBanner>
        </div>

        <div className="mt-8">
          <InlineGrid columns={{ xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }} gap={'400'}>
            <TextField
              autoComplete="off"
              type="number"
              value={props.orderToShipDays}
              onChange={(value) => {
                const newValue = Math.max(0, Math.min(365, Number(value))).toString();
                props.onChangeOrderToShipDays(newValue);
              }}
              label={
                <div className="d-flex">
                  <RegularText>Minimum preparation days</RegularText>
                  <Tooltip content={description.orderToShipDaysDescription}>
                    <span className="ml-4">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              }
              helpText={
                maxPrep < minPrep || minDelivery < minPrep || maxDelivery < minPrep ? (
                  <RegularText tone="caution">Minimum preparation days is incorrect</RegularText>
                ) : null
              }
            />

            <TextField
              autoComplete="off"
              type="number"
              value={props.prepDays}
              onChange={(value) => {
                const newValue = Math.max(0, Math.min(365, Number(value))).toString();
                props.onChangePrepDays(newValue);
              }}
              label={
                <div className="d-flex">
                  <RegularText>Maximum preparation days</RegularText>
                  <Tooltip content={description.prepDaysDescription}>
                    <span className="ml-4">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              }
              helpText={
                maxPrep < minPrep ? <RegularText tone="caution">Maximum preparation days is incorrect</RegularText> : null
              }
            />

            <TextField
              autoComplete="off"
              type="number"
              value={props.shipToReceiveDays}
              onChange={(value) => {
                const newValue = Math.max(0, Math.min(365, Number(value))).toString();
                props.onChangeShipToReceiveDays(newValue);
              }}
              label={
                <div className="d-flex">
                  <RegularText>Minimum delivery days</RegularText>
                  <Tooltip content={description.shipToReceiveDaysDescription}>
                    <span className="ml-4">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              }
              helpText={
                minDelivery < minPrep || maxDelivery < minDelivery ? (
                  <RegularText tone="caution">Minimum delivery days is incorrect</RegularText>
                ) : null
              }
            />

            <TextField
              autoComplete="off"
              type="number"
              value={props.estDays}
              onChange={(value) => {
                const newValue = Math.max(0, Math.min(365, Number(value))).toString();
                props.onChangeEstDays(newValue);
              }}
              label={
                <div className="d-flex">
                  <RegularText>Maximum delivery days</RegularText>
                  <Tooltip content={description.estDaysDescription}>
                    <span className="ml-4">
                      <InfoIcon />
                    </span>
                  </Tooltip>
                </div>
              }
              helpText={
                maxDelivery < minPrep || maxDelivery < minDelivery ? (
                  <RegularText tone="caution">Maximum delivery days is incorrect</RegularText>
                ) : null
              }
            />
          </InlineGrid>
        </div>

        {props.additionalContent}
      </CustomCard>
    </div>
  );
};

export default memo(EstimatedDateSetup);
